import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface InternalNavbarProps {
  links?: Array<{ label: string; href: string; internal?: boolean }>;
  rightCta?: { label: string; href: string };
  backTo?: { label: string; href: string };
}

export default function InternalNavbar({ links, rightCta, backTo }: InternalNavbarProps) {
  const { i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const isEn = i18n.language === "en";

  const toggleLanguage = () => {
    i18n.changeLanguage(isEn ? "es" : "en");
  };

  return (
    <nav className="w-full bg-white border-b border-stone-100 px-6 py-4 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://storage.readdy-site.link/project_files/eabe49ed-6fb1-4abe-9d8b-950f8fdeaeed/20102fc6-5b65-4c43-8938-3a6df6c13095_21-mar-2026-02_01_49.png?v=96141c8a356bac035317d0f45aa9e568"
            alt="Auria"
            className="h-8 w-auto"
          />
          <span className="text-base font-bold text-stone-900 tracking-tight">Auria</span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6 text-sm">
          {links?.map((link) =>
            link.internal !== false ? (
              <Link
                key={link.href}
                to={link.href}
                className="text-stone-500 hover:text-stone-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                className="text-stone-500 hover:text-stone-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </a>
            )
          )}

          {backTo && (
            <Link
              to={backTo.href}
              className="flex items-center gap-1.5 text-stone-500 hover:text-stone-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-base" />
              {backTo.label}
            </Link>
          )}

          {rightCta && (
            <Link
              to={rightCta.href}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-xs"
            >
              {rightCta.label}
            </Link>
          )}

          {/* Language toggle */}
          <button
            onClick={toggleLanguage}
            title={isEn ? "Cambiar a español" : "Switch to English"}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-200 text-stone-600 hover:border-stone-400 hover:text-stone-900 text-xs font-bold tracking-wide transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-translate-line text-sm" />
            {isEn ? "ES" : "EN"}
          </button>
        </div>

        {/* Mobile */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={toggleLanguage}
            className="text-xs font-bold px-2.5 py-1 rounded-full border border-stone-200 text-stone-600 cursor-pointer whitespace-nowrap"
          >
            {isEn ? "ES" : "EN"}
          </button>
          {(links || backTo) && (
            <button
              className="w-9 h-9 flex items-center justify-center cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl text-stone-800`} />
            </button>
          )}
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white border-b border-stone-100 px-6 py-4 flex flex-col gap-4 mt-1">
          {links?.map((link) =>
            link.internal !== false ? (
              <Link key={link.href} to={link.href} onClick={() => setMenuOpen(false)}
                className="text-sm font-medium text-stone-700 hover:text-stone-900 cursor-pointer">
                {link.label}
              </Link>
            ) : (
              <a key={link.href} href={link.href} onClick={() => setMenuOpen(false)}
                className="text-sm font-medium text-stone-700 hover:text-stone-900 cursor-pointer">
                {link.label}
              </a>
            )
          )}
          {rightCta && (
            <Link to={rightCta.href} className="text-sm font-semibold text-amber-600 cursor-pointer">
              {rightCta.label}
            </Link>
          )}
        </div>
      )}
    </nav>
  );
}
